import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { snowflakeId } from '../journey-accommodation/accommodationModel';
import { isMemoryAuthor } from './journeyMemoryModel';
import { linkedJourneyPublicationId, normalizeJourneyPublication } from '../journey-publication/journeyPublicationModel';

/** Bridge-supported SocialPostKind values only (no PLACE / JOURNEY_INVITATION). */
export const MEMORY_SHARE_POST_KINDS = Object.freeze(['MOOD', 'FOOD', 'JOURNEY']);

export const MEMORY_SHARE_POST_KIND_LABELS = Object.freeze({
  MOOD: '心情',
  FOOD: '美食',
  JOURNEY: '旅程',
});

export const MEMORY_SHARE_SNAPSHOT_NOTE = '這次分享會建立一篇獨立動態，之後修改日記不會自動更新動態。';

export const MEMORY_SHARE_PRIVATE_NOTE = '分享後會建立一篇公開動態，但原日記仍保持私人。';

export const MEMORY_CHAT_SHARE_CONFIRM = '要將這篇日記分享到旅程聊天室嗎？聊天室成員會看到這次分享的內容快照。';

export const MEMORY_CHAT_SHARE_PRIVATE_DISCLOSURE = '這篇私人日記目前只有你能看到。分享到旅程聊天室後，聊天室成員將能看到這次分享的內容快照。日記本身仍保持私人。';

export const MEMORY_SHARE_JOURNEY_DISABLED_NOTE = '需要先公開此旅程才能使用旅程動態。';

export function memorySharePostKindLabel(kind) {
  return MEMORY_SHARE_POST_KIND_LABELS[String(kind || '')] || String(kind || '');
}

/** Author ACTIVE non-PRIVATE_NOTE Memory may open Share. */
export function canShareMemoryToSocial(entry, viewerMemberId) {
  if (!entry || !isMemoryAuthor(entry, viewerMemberId)) return false;
  if (!entry.isActive) return false;
  if (entry.memoryType === 'PRIVATE_NOTE') return false;
  return true;
}

/** Author ACTIVE non-PRIVATE_NOTE Memory may share a snapshot to Journey Chat. PRIVATE is allowed. */
export function canShareMemoryToChat(entry, viewerMemberId) {
  return canShareMemoryToSocial(entry, viewerMemberId);
}

export function shareCoverPreviewFromMemory(entry) {
  const media = Array.isArray(entry?.media) ? entry.media : [];
  if (!media.length) {
    return { objectKey: '', url: '', hasCover: false };
  }
  const first = [...media].sort((a, b) => (
    Number(a.displayOrder) - Number(b.displayOrder)
    || String(a.mediaId || '').localeCompare(String(b.mediaId || ''))
  ))[0];
  const objectKey = first?.objectKey ? String(first.objectKey).trim() : '';
  return {
    objectKey,
    url: objectKey ? (first.url || resolveMemberMediaUrl(objectKey)) : '',
    hasCover: Boolean(objectKey),
  };
}

export function initialShareTextFromMemory(entry) {
  const content = entry?.content == null ? '' : String(entry.content).trim();
  if (content) return content;
  return '';
}

export function createShareClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `share-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

export function emptyShareDraft(entry) {
  return {
    postKind: 'MOOD',
    textContent: initialShareTextFromMemory(entry),
    attachJourney: false,
  };
}

export function validateShareDraft(draft, { journeyPublicationId } = {}) {
  const postKind = draft?.postKind;
  if (!MEMORY_SHARE_POST_KINDS.includes(postKind)) {
    return '請選擇動態類型。';
  }
  if (postKind === 'JOURNEY' && !journeyPublicationId) {
    return MEMORY_SHARE_JOURNEY_DISABLED_NOTE;
  }
  const text = String(draft?.textContent || '').trim();
  if ((postKind === 'MOOD' || postKind === 'FOOD') && !text) {
    return '請填寫動態內容。';
  }
  if (text.length > 4000) {
    return '動態內容最多 4000 字。';
  }
  return '';
}

/**
 * Build bridge request. Never sends coverFileReference (backend snapshots Memory media).
 * PD-ATT-01：MOOD／FOOD 可選附行程；勾選 attachJourney 且有可讀 publication 時送出。
 */
export function buildPublishMemorySocialPayload(draft, {
  clientRequestId,
  journeyPublicationId,
} = {}) {
  const postKind = draft?.postKind;
  const text = draft?.textContent == null || String(draft.textContent).trim() === ''
    ? null
    : String(draft.textContent).trim();
  const body = {
    postKind,
    textContent: text,
    clientRequestId: String(clientRequestId || '').trim(),
    confirm: true,
  };
  if (postKind === 'JOURNEY' && journeyPublicationId) {
    body.linkedJourneyPublicationId = String(journeyPublicationId);
  }
  if ((postKind === 'MOOD' || postKind === 'FOOD')
      && draft?.attachJourney
      && journeyPublicationId) {
    body.linkedJourneyPublicationId = String(journeyPublicationId);
  }
  return body;
}

export function normalizeMemorySocialPublication(value) {
  return {
    socialPostId: snowflakeId(value?.socialPostId),
    postKind: value?.postKind == null ? '' : String(value.postKind),
    status: value?.status == null ? '' : String(value.status),
    visibility: value?.visibility == null ? '' : String(value.visibility),
    textContent: value?.textContent == null ? '' : String(value.textContent),
    coverFileReference: value?.coverFileReference == null ? '' : String(value.coverFileReference),
    linkedJourneyPublicationId: snowflakeId(value?.linkedJourneyPublicationId),
    sourceMemoryEntryId: snowflakeId(value?.sourceMemoryEntryId),
    sourceJourneyId: snowflakeId(value?.sourceJourneyId),
    rowVersion: value?.rowVersion == null ? null : String(value.rowVersion),
    replayed: Boolean(value?.replayed),
    publishedAt: value?.publishedAt || null,
  };
}

/**
 * Find PUBLISHED JourneyPublication for this private journey via Creator Journeys
 * (formal public projection list — includes journeyId + publicationId).
 */
export function findPublishedJourneyPublicationId(creatorJourneysPayload, journeyId) {
  const target = journeyId == null ? '' : String(journeyId);
  if (!target) return null;
  const items = Array.isArray(creatorJourneysPayload?.items)
    ? creatorJourneysPayload.items
    : (Array.isArray(creatorJourneysPayload) ? creatorJourneysPayload : []);
  const hit = items.find((item) => String(item?.journeyId) === target && item?.publicationId != null);
  return hit ? snowflakeId(hit.publicationId) : null;
}

/** Owner management GET — unlock JOURNEY share only when PUBLISHED + PUBLIC. */
export function linkedJourneyPublicationIdFromManagement(payload) {
  return linkedJourneyPublicationId(normalizeJourneyPublication(payload));
}

export function classifyMemoryShareError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || cause?.message || '');
  const message = String(cause?.raw?.message || cause?.message || '');
  if (code === 90003 || code === 90005 || code === 90006
      || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (key === 'travel.journey.not.found') return 'journey-not-found';
  if (key === 'travel.memory.not.found') return 'not-found';
  if (key === 'travel.memory.permission.denied') return 'forbidden';
  if (key === 'travel.memory.lifecycle.conflict') return 'lifecycle';
  if (key === 'travel.memory.im.unavailable' || code === 95104) return 'unavailable';
  if (key === 'travel.social.post.conflict') return 'idempotency-conflict';
  if (key === 'travel.memory.publication.validation.failed') {
    if (/PRIVATE_NOTE/i.test(message)) return 'not-publishable';
    if (/ACTIVE/i.test(message) || /HIDDEN|DELETED/i.test(message)) return 'not-publishable';
    if (/PUBLISHED JourneyPublication|JOURNEY publication/i.test(message)) return 'journey-publication';
    return 'validation';
  }
  if (key === 'travel.social.post.validation.failed') return 'social-validation';
  if (code === 95900 || key.includes('validation')) return 'validation';
  if (code === 95901) return 'forbidden';
  if (code === 95902) return 'not-found';
  if (code === 95903) return 'idempotency-conflict';
  if (!cause?.raw && /network|timeout|failed/i.test(message)) return 'network';
  return 'error';
}

export function memoryShareErrorMessage(kind, detail) {
  if (kind === 'unauthorized') return detail || '登入已過期，請重新登入。';
  if (kind === 'forbidden') return '你沒有權限分享這篇日記。';
  if (kind === 'not-found' || kind === 'journey-not-found') return detail || '日記或旅程不存在。';
  if (kind === 'lifecycle') return detail || '目前旅程狀態不允許分享。';
  if (kind === 'not-publishable') return detail || '這篇日記目前無法分享到動態。';
  if (kind === 'journey-publication') return MEMORY_SHARE_JOURNEY_DISABLED_NOTE;
  if (kind === 'idempotency-conflict') {
    return '這次分享請求與先前內容衝突，請調整內容後再試。';
  }
  if (kind === 'social-validation' || kind === 'validation') {
    return detail || '請檢查分享內容。';
  }
  if (kind === 'unavailable') return detail || '聊天室目前無法使用，請稍後再試。';
  if (kind === 'network') return detail || '網路異常，請稍後再試。';
  return detail || '分享失敗，請稍後再試。';
}
